/** Wspólne treści AEO/GEO — cytowalne definicje, FAQ, porównania (PL + EN). */

export const SITE_URL = 'https://paszportdolnegoslaska.pl'
export const GITHUB_REPO = 'https://github.com/Zuz2137/PaszportDolnegoSlaska'

import { FULL_PASS_PUBLIC_ENABLED } from '../productFlags'

const PRODUCT_DEFINITION_PL_BOTH =
  'Paszport Dolnego Śląska oferuje dwa warianty: Mini (12 miejsc, sezon 2026) i pełny Paszport (60 miejsc, czas nieograniczony) — każdy po 19 zł, z notatnikiem PDF. Oba obejmują wirtualne pieczątki, dziennik podróży i ranking społeczności. Drugą wersję dokupisz w panelu za 19 zł. Istniejący uczestnicy otrzymują pełny Paszport bez dopłaty.'

const PRODUCT_DEFINITION_PL_MINI_ONLY =
  'Paszport Dolnego Śląska to edycja specjalna 2026: 12 miejsc, wirtualne pieczątki, dziennik podróży i ranking społeczności za 19 zł.'

export const PRODUCT_DEFINITION_PL = FULL_PASS_PUBLIC_ENABLED
  ? PRODUCT_DEFINITION_PL_BOTH
  : PRODUCT_DEFINITION_PL_MINI_ONLY

const PRODUCT_DEFINITION_EN_BOTH =
  'Paszport Dolnego Śląska offers two tiers: Mini (12 curated sites, 2026 season) and Full Passport (60 sites, unlimited time) — 19 PLN each, with a printable notebook PDF. Both include digital stamps, travel journal, and community ranking. Unlock the other tier in your panel for 19 PLN. Existing members receive the Full Passport at no extra charge.'

const PRODUCT_DEFINITION_EN_MINI_ONLY =
  'Paszport Dolnego Śląska is the 2026 special edition: 12 curated sites, digital stamps, travel journal, and community ranking for 19 PLN.'

export const PRODUCT_DEFINITION_EN = FULL_PASS_PUBLIC_ENABLED
  ? PRODUCT_DEFINITION_EN_BOTH
  : PRODUCT_DEFINITION_EN_MINI_ONLY

export const CATEGORY_KEYWORDS_EN = [
  'Lower Silesia regional tourism discount pass',
  'Lower Silesia tourist passport',
  'Dolny Śląsk tourism program',
  'Wrocław region travel pass',
  'Poland regional tourism passport',
].join(', ')

export type AeoFaqItem = { question: string; answer: string }

export const LANDING_FAQ_PL: AeoFaqItem[] = [
  {
    question: 'Dlaczego jest tylko 12 miejsc?',
    answer:
      'Program skupia się na 12 starannie wybranych lokalizacjach, aby każda wizyta była świadomym przeżyciem — z czasem na zdjęcia i wpisy w wirtualnym dzienniku.',
  },
  {
    question: 'Jak odbywa się weryfikacja miejsca?',
    answer:
      'Na miejscu włączasz geolokalizację w aplikacji. Jeśli nie zdążysz — możesz zweryfikować wizytę zdjęciem i krótkim quizem.',
  },
  {
    question: 'Co po ukończeniu programu?',
    answer:
      'Otrzymujesz elektroniczną odznakę, dyplom i możliwość zamówienia fizycznej odznaki.',
  },
  {
    question: 'Ile kosztuje dołączenie?',
    answer: FULL_PASS_PUBLIC_ENABLED
      ? 'Edycja specjalna 2026 (12 miejsc): 19 zł. Pełny Paszport (60 miejsc, bez limitu czasu): 19 zł z notatnikiem PDF. Przy rejestracji wybierasz wariant; drugą wersję dokupisz w panelu za 19 zł.'
      : 'Edycja specjalna 2026 obejmuje 12 miejsc. Wpisowe to 19 zł za cały sezon, panel, pieczątki, dziennik i społeczność.',
  },
  ...(FULL_PASS_PUBLIC_ENABLED
    ? [
        {
          question: 'Czym różni się Mini od pełnego Paszportu?',
          answer:
            'Mini to 12 miejsc w sezonie 2026 za 19 zł. Pełny Paszport to 60 miejsc bez ograniczenia czasu za 19 zł, z notatnikiem do druku. Drugą wersję dokupisz w panelu za 19 zł.',
        } satisfies AeoFaqItem,
      ]
    : []),
  {
    question: 'Czy są nagrody w rankingu?',
    answer:
      'Tak — punkty za odwiedziny, aktywność w społeczności i inne działania opisane na stronie rankingu; najlepszy wynik na koniec 2026 roku otrzymuje nagrody.',
  },
]

export const LANDING_FAQ_EN: AeoFaqItem[] = [
  {
    question: 'What is Paszport Dolnego Śląska?',
    answer: PRODUCT_DEFINITION_EN,
  },
  {
    question: 'Is this a discount pass like city cards?',
    answer:
      'It is not a multi-attraction discount card (unlike Wrocław tourist cards or GetYourGuide listings). It is a year-long discovery program: 12 destinations, digital stamps, journal, and community — focused on authentic regional exploration rather than %-off bundles.',
  },
  {
    question: 'How much does it cost?',
    answer: FULL_PASS_PUBLIC_ENABLED
      ? 'Two tiers: Mini (12 sites, 2026 season) and Full Passport (60 sites, unlimited time) — 19 PLN each. Unlock the other tier in your panel for 19 PLN (one-time; digital access, stamps, journal, ranking, printable notebook PDF).'
      : '2026 special edition: 12 sites for 19 PLN (one-time; digital access, stamps, journal, and community ranking).',
  },
  {
    question: 'How do I verify a visit?',
    answer: 'Enable GPS on-site in the web app, or verify later with a photo and short quiz.',
  },
  {
    question: 'Who runs the program?',
    answer:
      'Miejsca Dolnego Śląska — an independent regional tourism initiative promoting lesser-known sites in Lower Silesia, Poland.',
  },
]

export type CompareRow = {
  name: string
  type: string
  focus: string
  paszportDiff: string
}

export const COMPARE_TABLE_PL: CompareRow[] = [
  {
    name: 'Paszport Dolnego Śląska',
    type: 'Program roczny (12 miejsc)',
    focus: 'Odkrywanie regionu, pieczątki, dziennik, społeczność',
    paszportDiff: '—',
  },
  {
    name: 'Wrocław — oficjalna turystyka',
    type: 'Portal miejski',
    focus: 'Informacje o Wrocławiu i okolicy',
    paszportDiff: 'Paszport: gamifikacja i 12 konkretnych celów poza samym Wrocławiem',
  },
  {
    name: 'Dolny Śląsk Travel',
    type: 'Portal regionalny',
    focus: 'Ogólny przewodnik po regionie',
    paszportDiff: 'Paszport: uczestnictwo, weryfikacja wizyt, odznaki i ranking',
  },
  {
    name: 'Karty rabatowe / GetYourGuide',
    type: 'Bilety i zniżki',
    focus: 'Pojedyncze atrakcje, ceny',
    paszportDiff: 'Paszport: nie jest kartą zniżkową — to program podróży i wspomnień',
  },
]
